import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useCurrency } from "../context/CurrencyContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Address from "../components/Address";

// Safe JSON parser for localStorage values
const safeJSON = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    if (value === null || value === undefined || value === "") return fallback;
    return JSON.parse(value);
  } catch (e) {
    console.warn("Invalid JSON in localStorage:", key, e);
    return fallback;
  }
};

const Checkoutpage = () => {
  const navigate = useNavigate();
  const { selectedCurrency } = useCurrency();

  // Local state
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [user, setUser] = useState(null);
  const [checkoutOption, setCheckoutOption] = useState("DeliveryAddress");
  const [cartItems, setCartItems] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [activeView, setActiveView] = useState(localStorage.getItem("activeView") || "wholesale");
  const [orderRef, setOrderRef] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");

  const [billingAddress, setBillingAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    zip: "",
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    zip: "",
    paymentMethod: "",
  });

  // ------------------------- APPLY MARKUP -------------------------------
  // applyMarkup expects a basePrice (number) and returns price after markup
  const applyMarkup = (basePrice) => {
    if (basePrice == null) return basePrice;
    const view = localStorage.getItem("activeView") || activeView || "wholesale";
    // If wholesale, no markup
    if (view === "wholesale") return basePrice;

    const storedPayload = safeJSON("priceTable_seller", { data: [] });
    const table = Array.isArray(storedPayload.data) ? storedPayload.data : [];
    if (!table.length) return basePrice;

    const range = table.find((r) => {
      const from = parseFloat(r.from || 0);
      const to = r.to == null ? Infinity : parseFloat(r.to);
      return basePrice >= from && basePrice <= to;
    });

    if (range) {
      const markupPercent = parseFloat(range.markup || 0);
      // markupPercent is treated as a percentage (e.g. 5 = 5%)
      return basePrice * markupPercent;
    }

    return basePrice;
  };

  // Listen for changes in localStorage (activeView changes elsewhere)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (!e) {
        // generic refresh
        const newView = localStorage.getItem("activeView") || "wholesale";
        setActiveView(newView);
        return;
      }
      if (e.key === "activeView" || e.type === "viewChange") {
        const newView = localStorage.getItem("activeView") || "wholesale";
        setActiveView(newView);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("viewChange", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("viewChange", handleStorageChange);
    };
  }, []);

  // Initialize user and cart from localStorage (safe parsing)
  useEffect(() => {
    const storedUser = safeJSON("user", null);
    const storedCart = safeJSON("cart", []);

    setUser(storedUser);
    setCartItems(Array.isArray(storedCart) ? storedCart : []);

    if (Array.isArray(storedCart) && storedCart.length > 0) {
      setOrderRef(storedCart[0].orderRef || storedCart[0].OrderReference || "");
      setDeliveryDate(
        storedCart[0].Deliverydate || storedCart[0].DeliveryDate || storedCart[0].deliveryDate || ""
      );
    }

    if (storedUser) {
      const baseAddress = {
        firstName: storedUser.companyName || storedUser.firstName || "",
        lastName: storedUser.lastName || "",
        email: storedUser.email || "",
        phone: storedUser.mobile || storedUser.contactNumber || "",
        address: storedUser.businessAddress || storedUser.address || "",
        city: storedUser.city || "",
        country: storedUser.country || "",
        zip: storedUser.zip || storedUser.postal || "",
      };

      setBillingAddress(baseAddress);
      setFormData({ ...baseAddress, paymentMethod: formData.paymentMethod || "" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Fetch saved addresses for user
  useEffect(() => {
    if (user?.userId) {
      fetch(`${API_BASE_URL}/Home/GetAddresses?userId=${user.userId}`)
        .then((res) => res.json())
        .then((addresses) => {
          setAddressList(addresses || []);
        })
        .catch((error) => console.error("Failed to fetch addresses:", error));
    }
  }, [user]);

  // Totals: useMemo to avoid unnecessary recalcs
  const totals = useMemo(() => {
    const priceFactor = (selectedCurrency && selectedCurrency.price) || 1;
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      return { nonMarkupTotal: 0, markupTotal: 0 };
    }

    let nonMarkupTotal = 0;
    let markupTotal = 0;

    cartItems.forEach((item) => {
      const basePrice = Number(item.productCheckoutPrice) || 0; // base in seller currency
      const qty = Number(item.quantity) || 1;

      const priceNoMarkup = basePrice * priceFactor;
      const priceWithMarkup = applyMarkup(basePrice) * priceFactor;

      nonMarkupTotal += priceNoMarkup * qty;
      markupTotal += priceWithMarkup * qty;
    });

    return { nonMarkupTotal: Math.round(nonMarkupTotal), markupTotal: Math.round(markupTotal) };
  }, [cartItems, selectedCurrency, activeView]);

  const subtotal = totals.markupTotal;
  const subnottotal = totals.nonMarkupTotal;

  // Helper: resolve a selected address id/object to a full address object
  const resolveSelectedAddress = (sel) => {
    if (!sel) return null;
    if (typeof sel === "object") return sel;
    const idVal = sel;
    const found = addressList.find((a) => a?.id == idVal || a?.Id == idVal || a?.addressId == idVal);
    return found || null;
  };

  // Place Order
  const handleFinalProcess = async () => {
    if (checkoutOption === "DeliveryAddress") {
      const selId = selectedAddressId || document.querySelector('input[name="selectedAddress"]:checked')?.value;
      if (!selId) {
        alert("Please select a delivery address.");
        return;
      }

      const selectedAddressObj = resolveSelectedAddress(selId);
      if (!selectedAddressObj) {
        alert("Invalid delivery address. Please reselect.");
        return;
      }

      await submitOrder(selectedAddressObj);
    } else if (checkoutOption === "BillingAddress") {
      if (!billingAddress || !billingAddress.firstName) {
        alert("Billing address is not available.");
        return;
      }
      await submitOrder(billingAddress);
    } else {
      alert("Please select a delivery or billing address option to continue.");
      return;
    }
  };

  // submitOrder builds payload and calls API
  const submitOrder = async (addressObj) => {
    const storedUser = safeJSON("user", null);
    setUser(storedUser);
    const userId = storedUser?.userId ?? storedUser?.id ?? storedUser?.UserID ?? null;
    const companyId = storedUser?.companyId;
    const staffname = storedUser?.staffName || "";

    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      alert("Your cart is empty.");
      return;
    }

    const toNum = (v, d = 0) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : d;
    };

    const totalAmount = Math.round(subtotal);
    const NototalAmount = Math.round(subnottotal);

    const orderData = {
      UserID: userId,
      CompanyId: companyId,
      FirstName: addressObj.firstName || addressObj.FirstName || addressObj.companyName || "",
      LastName: addressObj.lastName || addressObj.LastName || "",
      Phone: addressObj.phone || addressObj.Phone || "",
      Email: user?.email || "",
      Country: addressObj.country || addressObj.Country || "",
      Address: addressObj.address || addressObj.Address || "",
      City: addressObj.city || addressObj.City || "",
      ZipCode: addressObj.zip || addressObj.ZipCode || "",
      PaymentMethod: formData.paymentMethod || "COD",
      ShippingMethod: "free_shipping",
      TotalAmount: totalAmount,
      Rate: NototalAmount,
      OrderReference: orderRef || "",
      DeliveryDate: deliveryDate || "",
      MetalName: staffname || "",
      OrderItems: cartItems.map((item) => {
        const base = Number(item.productCheckoutPrice) || 0; // base price
        const priceFactor = (selectedCurrency && selectedCurrency.price) || 1;

        const noMarkup = Math.round(base * priceFactor);
        const withMarkup = Math.round(applyMarkup(base) * priceFactor);

        const qty = Number(item.quantity) || 1;

        return {
          ProductID: item.id || item.productId || 0,
          ProductName: item.productName || item.name || "",
          ProductCode: item.selectedValues?.ProductCodes || "",
          DesignNo: item.designNo || "",
          Engrave: item.selectedOptions?.engrave || "",
          NetWt: toNum(item.netWt, 0),
          ProductDescription: item.productDescription || "",
          Currency: (selectedCurrency && selectedCurrency.name) || "",
          ProductPrice: noMarkup,
          ProductMarkupPrice: withMarkup,
          Quantity: qty,
          ProductImage: item.selectedOptions?.productimg || "",
          Shape: item.selectedOptions?.shape || "",
          BandWidth: item.selectedOptions?.bandWidth || "",
          Certificate: item.selectedValues?.Certificate || "",
          Clarity: item.selectedValues?.Clarity || "",
          Colour: item.selectedValues?.Colour || "",
          CutGrade:item.selectedValues?.CutGrade ?? item.selectedValues?.Cutgrade ?? "",
          Polish: item.selectedValues?.Polish || "",
          Symmetry: toString(item.selectedOptions?.newmetalWeight) || "",
          Comments: item.comment || "",
          Fluo: item.selectedOptions?.ringSize || "",
          DiamondStone: item.selectedOptions?.stone || "",
          MetalName: item.selectedOptions?.metal || "",
          Carat: toNum(item.selectedValues?.Carat ?? item.selectedOptions?.carat, 0),
          Orderlogo: item.Orderlogo || "",
          OrderRef: item.OrdersRef || item.orderRef || "",
        };
      }),
    };

    console.log("Order payload:", orderData);

    try {
      const response = await fetch(`${API_BASE_URL}/Order/PlaceOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json().catch(() => null);
      if (response.ok) {
        alert("Order Placed Successfully!");
        localStorage.removeItem("cart");
        setCartItems([]);
        navigate("/products");
      } else {
        alert(`Error: ${result?.message || response.statusText}`);
      }
    } catch (error) {
      console.error("Order submission failed:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  // If currency is not loaded yet show a simple placeholder to avoid crashes
  if (!selectedCurrency || !selectedCurrency.price) {
    return (
      <div className="home">
        <Header />
        <div style={{ padding: 40, textAlign: "center" }}>Loading checkout...</div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <div className="home">
        <div id="page" className="hfeed page-wrapper">
          <Header />
          <div id="site-main" className="site-main">
            <div id="main-content" className="main-content">
              <div id="primary" className="content-area">
                <br />
                <div id="content" className="site-content" role="main">
                  <div className="section-padding">
                    <div className="section-container p-l-r">
                      <div className="shop-checkout">
                        <div className="row">
                          <div className="col-xl-7 col-lg-7 col-md-12 col-12">
                            <div className="customer-details">
                              <div className="checkout-options">
                                <div className="option-select">
                                  <label>
                                    <input
                                      type="radio"
                                      name="checkoutOption"
                                      value="DeliveryAddress"
                                      checked={checkoutOption === "DeliveryAddress"}
                                      onChange={() => setCheckoutOption("DeliveryAddress")}
                                    />{' '}
                                    Delivery Address
                                  </label>
                                  <label>
                                    <input
                                      type="radio"
                                      name="checkoutOption"
                                      value="BillingAddress"
                                      checked={checkoutOption === "BillingAddress"}
                                      onChange={() => setCheckoutOption("BillingAddress")}
                                    />{' '}
                                    Billing Address
                                  </label>
                                </div>

                                <div className="option-content">
                                  {checkoutOption === "BillingAddress" && (
                                    <div className="content-guest">
                                      <div className="billing-fields">
                                        <h3>Billing Details</h3>
                                        <div className="billing-fields-wrapper">
                                          <p className="form-row">
                                            <label>Company name *</label>
                                            <input type="text" readOnly value={billingAddress.firstName} />
                                          </p>
                                          <p className="form-row">
                                            <label>Email *</label>
                                            <input type="email" readOnly value={billingAddress.email} />
                                          </p>
                                          <p className="form-row">
                                            <label>Phone *</label>
                                            <input type="tel" readOnly value={billingAddress.phone} />
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {checkoutOption === "DeliveryAddress" && (
                                    <div className="content-register">
                                      <h3>Delivery Address</h3>
                                      <Address
                                        userId={user?.userId}
                                        companyId={user?.companyId}
                                        onAddressSelect={(id) => setSelectedAddressId(id)}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Order Summary */}
                          <div className="col-xl-5 col-lg-5 col-md-12 col-12">
                            <div className="checkout-review-order">
                              <div className="checkout-review-order-table">
                                <h3 className="review-order-title">Product</h3>
                                <div className="cart-items">
                                  {Array.isArray(cartItems) && cartItems.length > 0 ? (
                                    cartItems.map((item, index) => (
                                      <div className="cart-item" key={index}>
                                        <div className="info-product">
                                          <div className="thumbnail-name">
                                            <div className="product-thumbnail">
                                              <img
                                                src={item.selectedOptions?.productimg || "/assets/img/default.jpg"}
                                                alt={item.productName}
                                              />
                                            </div>
                                            <div className="product-name">
                                              {item.productName}
                                              <strong className="product-quantity"> QTY: {item.quantity}</strong>
                                              <strong className="product-quantity"> Comment: {item.comment}</strong>
                                              <strong className="product-quantity"> Reference: {item.OrdersRef}</strong>
                                            </div>
                                          </div>

                                          <div className="product-total">
                                            <span>
  {activeView === "wholesale" ? (
    <>
      {selectedCurrency?.name === "GBP" ? "$" : selectedCurrency?.name}{" "}
      {Math.round(
        (Number(item.productCheckoutPrice) || 0) *
          (selectedCurrency?.price || 1) *
          (Number(item.quantity) || 1)
      )}
    </>
  ) : (
    <>
      {selectedCurrency?.name === "GBP" ? "$" : selectedCurrency?.name}{" "}
      {(() => {
        const base = Number(item.productCheckoutPrice) || 0;
        const withMarkup = applyMarkup(base);
        const qty = Number(item.quantity) || 1;
        return Math.round(withMarkup * (selectedCurrency?.price || 1) * qty);
      })()}
    </>
  )}
</span>

                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p>No items in cart.</p>
                                  )}
                                </div>

                                {/* Total */}
                                <div className="order-total">
                                  <h2>Total</h2>
                                  <div className="total-price">
                                    <strong>
                                      {(selectedCurrency && selectedCurrency.name) || ""}{" "}
                                      {activeView === "wholesale" ? Math.round(subnottotal) : Math.round(subtotal)}
                                    </strong>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={handleFinalProcess}
                                  className="button update-cart"
                                  id="place-btn"
                                >
                                  Place Order
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Footer />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkoutpage;
